import { useForm } from 'components/Form';
import { useEffect, useMemo, useRef } from 'react';
import { useAPIPlanSettings } from 'repo/system/plansRepo';
import { eFormMode, eLimitedTextName } from './enums';
import { convertUsageIdsIntoForm } from './utils';

export const EMAIL_WORD_COUNT = 1000;
export const SMS_WORD_COUNT = 70;

export const useClientsForm = (initialValue, mode) => {
    const isResetForm = useRef(false);
    const [fetchPlans, responsePlans] = useAPIPlanSettings();
    const [formData, handleChangeData, updateData] = useForm(initialValue);
    const tempDemoExpiresAtRef = useRef(null);

    const plans = responsePlans?.data?.data || null;

    const planOptions = useMemo(() => {
        if (!Array.isArray(plans)) return [];

        // Define the desired order: Simple, Lite, Pro, Standard, Demo, then others
        const priorityOrder = ['Simple', 'Lite', 'Pro', 'Standard', 'Demo'];

        // Sort plans according to the priority order
        const sortedPlans = plans.sort((a, b) => {
            const aIndex = priorityOrder.indexOf(a.name);
            const bIndex = priorityOrder.indexOf(b.name);

            // If both plans are in priority list, sort by their index
            if (aIndex !== -1 && bIndex !== -1) {
                return aIndex - bIndex;
            }

            // If only 'a' is in priority list, it comes first
            if (aIndex !== -1) return -1;

            // If only 'b' is in priority list, it comes first
            if (bIndex !== -1) return 1;

            // If neither is in priority list, maintain original order
            return 0;
        });

        return sortedPlans.map(plan => ({ value: plan.id, label: plan.name }));
    }, [plans]);
    const handleLimitedTextInput = type => e => {
        const maxCount = type === eLimitedTextName.email ? EMAIL_WORD_COUNT : SMS_WORD_COUNT;
        if (formData[e.target.name].length >= maxCount && e.target.value.length > maxCount) {
            // skip
            return;
        }
        if (e.target.value.length > maxCount) {
            handleChangeData({
                target: {
                    name: e.target.name,
                    value: e.target.value.substring(0, maxCount),
                },
            });
            return;
        }
        handleChangeData(e);
    };

    // Watch Client Usages change
    useEffect(() => {
        if (isResetForm.current) {
            isResetForm.current = false;
            return;
        }
        if (!formData?.plan_id && mode === eFormMode.update) return;

        let selectedPlan = null;
        if (Array.isArray(plans)) {
            selectedPlan = plans.find(plan => plan.id === formData.plan_id);

            if (!selectedPlan && plans.length > 1) {
                selectedPlan = plans[0];
            }
        }

        if (selectedPlan) {
            updateData({
                ...formData,
                ...convertUsageIdsIntoForm(selectedPlan.usage_ids),
                categories_threshold: selectedPlan.categories_threshold,
                users_threshold: selectedPlan.users_threshold,
                admins_threshold: selectedPlan.admins_threshold,
                is_demo: selectedPlan.name === 'Demo',
            });
        }
    }, [formData?.plan_id]);

    useEffect(() => {
        updateData('allowFileUploadLabel', formData.allowFileUpload ? 'Yes' : 'No');
    }, [formData?.allowFileUpload]);

    // Handle DatePicker onChange for demo_expires_at
    const handleDemoExpiresAtChange = event => {
        handleChangeData({
            target: {
                name: 'demo_expires_at',
                value: event.target.value,
            },
        });
    };

    useEffect(() => {
        if (!formData?.is_demo) {
            if (formData?.demo_expires_at) {
                tempDemoExpiresAtRef.current = formData.demo_expires_at;
            }
            updateData('demo_expires_at', null);
        } else {
            if (tempDemoExpiresAtRef.current !== null) {
                updateData('demo_expires_at', tempDemoExpiresAtRef.current);
                tempDemoExpiresAtRef.current = null;
            }
        }
    }, [formData?.is_demo]);

    useEffect(() => {
        fetchPlans();
    }, []);

    // Set Standard plan as default for new client creation
    useEffect(() => {
        if (
            Array.isArray(plans) &&
            plans.length > 0 &&
            mode === eFormMode.create &&
            !formData.plan_id
        ) {
            // Find Standard plan by name
            const standardPlan = plans.find(plan => plan.name === 'Standard');
            if (standardPlan) {
                updateData({
                    ...formData,
                    plan_id: standardPlan.id,
                });
            }
        }
    }, [plans, mode, formData.plan_id]);

    const resetForm = newData => {
        isResetForm.current = true;
        updateData(newData);
    };

    return {
        handleLimitedTextInput,
        formData,
        handleChangeData,
        planOptions,
        resetForm,
        handleDemoExpiresAtChange,
    };
};
